import { useCallback, useContext, useState } from "react";
import { Dataset } from "../api/Datasets";
import { useFileStructure } from "../Dataset/common/Tree/Folder/helpers/FileHelpers";
import { showtoast } from "../common/overlays/Toasts/showtoast";
import { usePostMutation } from "../api/BaseEntityApi";
import { ResourceName } from "../main/Routing";
import { useQuery } from "@tanstack/react-query";
import { SessionContext } from "../common/contexts/SessionContext";
import { getLoadingState } from "../common/LoadingWrapper";

interface MNovaFile {
  name: string;
  data: Uint8Array;
}

export interface MWebLoadingState {
  message: string;
  progress: number;
}
export interface MWebLicense {
  uuid: string;
  name: string;
  date: string;
  expiry_date: string;
  features: string[];
  signature: string;
}

interface UseMWebProps {
  datasetId: Dataset["id"];
}
export const useMWeb = ({ datasetId }: UseMWebProps) => {
  const { api } = useContext(SessionContext);
  const { mutateAsync } = usePostMutation<ArrayBuffer, { path: string; responseType: string }>({
    resource: `datasets/${datasetId}/file` as ResourceName,
  });
  const [MWebFinished, setMWebFinished] = useState(false);
  const [MWebLoadingStatus, setMWebLoadingStatus] = useState<MWebLoadingState>();

  const [MWebError, setMWebError] = useState<string>();
  const filesStructureQuery = useFileStructure(datasetId);
  const { data: filesStructure } = filesStructureQuery;

  const licenceQuery = useQuery({
    queryKey: ["mweb", "license"],
    queryFn: async () => JSON.parse(await api.get(`mweb/license`, null)) as Promise<MWebLicense>,
  });

  const { data: license } = licenceQuery;

  let MNovaFile: string | undefined = undefined;

  if (filesStructure !== undefined) {
    const mnovaFiles = Object.keys(filesStructure).filter((key) => key.endsWith(".mnova"));
    if (mnovaFiles.length > 0) {
      MNovaFile = mnovaFiles[0];
    }
  }

  const MWebFetchFunc = useCallback(
    async (mnovaFile: string): Promise<MNovaFile> => {
      var data = await mutateAsync({ body: { path: mnovaFile, responseType: "blob" } }).catch(() => {
        console.error("Error fetching file");
        return null;
      });
      if (data) {
        return { name: mnovaFile, data: new Uint8Array(data) };
      } else {
        return { name: mnovaFile, data: new Uint8Array(0) };
      }
    },
    [mutateAsync]
  );

  const MWebFileSaveFunc = useCallback((name: string, data: Uint8Array) => {
    showtoast("success", `Saving ${name} with ${data.length} bytes`);
  }, []);

  const MWebloadingFunc = useCallback((state: MWebLoadingState) => {
    setMWebLoadingStatus(state);
  }, []);

  const MWebFinishedCallback = useCallback(() => {
    setMWebFinished(true);
  }, []);

  const MWebErrorCallback = useCallback((errorText: any) => {
    // showtoast("error", `Mweb Error: ${errorText}`);
    setMWebError(errorText);
  }, []);

  // var { status, fetchStatus,  } = combineLoadingStates(
  //   [filesStructureStatus, licenseStatus],
  //   [filesStructureFetchStatus, licenseFetchStatus]
  // );

  const { status, fetchStatus, error } = getLoadingState([filesStructureQuery, licenceQuery]);

  return {
    datasetId,
    MNovaFile,
    MWebFetchFunc,
    MWebFileSaveFunc,
    MWebloadingFunc,
    MWebFinishedCallback,
    MWebErrorCallback,
    MWebFinished,
    MWebLoadingStatus,
    MWebError,
    error,
    fetchStatus,
    status,
    license,
  };
};
